import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6p9k6ba90d3mf3eftjg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/806d6ecf-275d-4b30-92bb-c0ace41e3ffb.png" alt="APERTO SH.P.K." className="h-10 w-10 object-contain" />
            <span className={`text-lg font-semibold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>APERTO SH.P.K.</span>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>Home</button>
            <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>About</button>
            <button onClick={() => scrollToSection('products')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>Products</button>
            <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/bf6f469ce5fc79901af2ff7370f6b79e.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full text-center">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">Premium iOS Solutions<br />for Modern Users</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">VPN & Cleaner Applications Engineered for Performance and Security</p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-4">
            <span className="text-xs uppercase tracking-wider text-teal-600 font-semibold">/ABOUT US</span>
          </div>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 leading-snug mb-8">APERTO SH.P.K. is a team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.</h2>
            </div>
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500/10 rounded-full">
                    <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Security First</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">We prioritize the security and privacy of our users with military-grade encryption and advanced protection protocols.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500/10 rounded-full">
                    <i className="ri-rocket-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Performance Optimized</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">Our applications are built with cutting-edge technology to deliver seamless performance and exceptional user experience.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Products</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Innovative iOS applications designed to protect your privacy and optimize your device performance.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* VPN Product */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="inline-block bg-gray-900 text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-6 whitespace-nowrap">Security</div>
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20VPN%20application%20icon%20design%20for%20iOS%20with%20shield%20and%20lock%20elements%2C%20gradient%20blue%20and%20teal%20colors%2C%20minimalist%203D%20style%2C%20professional%20security%20app%20illustration%2C%20clean%20white%20background%2C%20centered%20composition%2C%20high%20quality%20digital%20artwork&width=400&height=400&seq=vpn-icon-001&orientation=squarish" 
                  alt="VPN Protection" 
                  className="w-48 h-48 mx-auto object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">VPN Protection</h3>
              <p className="text-gray-600 text-center mb-4 leading-relaxed">Secure your online activities with advanced encryption. Browse privately and access content from anywhere in the world with lightning-fast connection speeds.</p>
            </div>

            {/* Cleaner Product */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="inline-block bg-teal-600 text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-6 whitespace-nowrap">Optimization</div>
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20iOS%20cleaner%20application%20icon%20with%20broom%20and%20sparkle%20elements%2C%20gradient%20teal%20and%20green%20colors%2C%20minimalist%203D%20style%2C%20professional%20optimization%20app%20illustration%2C%20clean%20white%20background%2C%20centered%20composition%2C%20high%20quality%20digital%20artwork&width=400&height=400&seq=cleaner-icon-001&orientation=squarish" 
                  alt="Device Cleaner" 
                  className="w-48 h-48 mx-auto object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">Device Cleaner</h3>
              <p className="text-gray-600 text-center mb-4 leading-relaxed">Keep your iPhone running smoothly with intelligent cleaning. Remove junk files, optimize storage, and boost performance with just one tap.</p>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mt-24">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-500/10 rounded-xl mb-6">
                  <i className="ri-lock-password-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Advanced Encryption</h3>
                <p className="text-gray-600 leading-relaxed">Military-grade encryption protocols protect your data across all connections, ensuring complete privacy and security.</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-500/10 rounded-xl mb-6">
                  <i className="ri-flashlight-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">One-Tap Cleaning</h3>
                <p className="text-gray-600 leading-relaxed">Effortlessly clean your device with a single tap. Our intelligent algorithms identify and remove unnecessary files instantly.</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-500/10 rounded-xl mb-6">
                  <i className="ri-shield-check-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Real-Time Protection</h3>
                <p className="text-gray-600 leading-relaxed">Continuous monitoring and protection keep your device secure 24/7 with automatic threat detection and prevention.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <p className="text-lg text-gray-600">Get in touch with our team</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto mb-16">
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">ADDRESS</span>
                </div>
                <p className="text-lg text-gray-900 ml-11">AUTOSTRADA LEZHE - TIRANE, KM5</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-building-line text-xl text-teal-600"></i>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">CITY</span>
                </div>
                <p className="text-lg text-gray-900 ml-11">LEZHE</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">ZIP</span>
                </div>
                <p className="text-lg text-gray-900 ml-11">4500</p>
              </div>
            </div>
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-global-line text-xl text-teal-600"></i>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">COUNTRY</span>
                </div>
                <p className="text-lg text-gray-900 ml-11">Albania</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-mail-send-line text-xl text-teal-600"></i>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">EMAIL</span>
                </div>
                <a href="mailto:info@apertoshpk.com" className="text-lg text-teal-600 hover:text-teal-700 ml-11 cursor-pointer">info@apertoshpk.com</a>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-phone-line text-xl text-teal-600"></i>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">PHONE</span>
                </div>
                <a href="tel:+13605044426" className="text-lg text-teal-600 hover:text-teal-700 ml-11 cursor-pointer">+1 3605044426</a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto bg-gray-50 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Send us a message</h3>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  required 
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  required 
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows={5} 
                  required 
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>
              <button 
                type="submit" 
                className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/806d6ecf-275d-4b30-92bb-c0ace41e3ffb.png" alt="APERTO SH.P.K." className="h-8 w-8 object-contain" />
                <span className="text-lg font-bold">APERTO SH.P.K.</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">Developing innovative iOS applications that prioritize user experience and device security.</p>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-wider font-bold mb-4">PRODUCTS</h4>
              <ul className="space-y-3">
                <li><a href="#products" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">VPN Application</a></li>
                <li><a href="#products" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Cleaner Tool</a></li>
                <li><a href="#products" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Premium Features</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-wider font-bold mb-4">COMPANY</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#about" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Our Team</a></li>
                <li><a href="#contact" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-wider font-bold mb-4">SUPPORT</h4>
              <ul className="space-y-3">
                <li><a href="#contact" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Help Center</a></li>
                <li><a href="#" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <p className="text-sm text-gray-400 text-center">© 2025 APERTO SH.P.K. All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
}